import {useRouter} from '@tanstack/react-router';
import {useEffect, useRef} from 'react';

import {Route} from '~utils/routeUtil';
import {imageDataActions} from '~redux/reducers/imageReducer';
import {useAppDispatch} from '~redux/index';

export function useContextSwitchGalleryReset() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const previousContextRef = useRef<string | null>(null);

  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      const newContext = getContextFromRoute(url);
      const currentContext = previousContextRef.current;

      if (currentContext !== null && currentContext !== newContext) {
        dispatch(imageDataActions.resetItemsPerPage());
        // Clear stale total + pagination cursors so the next gallery fetches fresh.
        dispatch(imageDataActions.resetImages());
        // Reset the page number so the next gallery starts on page 1 instead of a stale page.
        dispatch(imageDataActions.resetOverviewPageNumber());
      }

      previousContextRef.current = newContext;
    };

    if (previousContextRef.current === null) {
      previousContextRef.current = getContextFromRoute(router.state.location.href);
    }

    const unsubscribe = router.subscribe('onBeforeNavigate', (event) =>
      handleRouteChangeStart(event.toLocation.pathname as Route),
    );
    return () => unsubscribe();
  }, [router, dispatch]);
}

// needed for image pages navigation
function getContextFromRoute(pathname: string): string {
  const cleanPath = pathname.split('?')[0];

  if (cleanPath.includes(Route.archive)) {
    return 'archive';
  } else if (cleanPath.includes(Route.modelSamples)) {
    return 'model-samples';
  } else if (cleanPath.includes(Route.modelAddSamples)) {
    return 'model-add-samples';
  } else if (cleanPath.includes(Route.modelFinetune)) {
    return 'model-finetune';
  } else if (cleanPath.includes(Route.simsearch)) {
    return 'simsearch';
  } else if (cleanPath.includes(Route.tasks)) {
    return 'tasks';
  } else if (cleanPath.includes(Route.models)) {
    return 'models';
  } else if (cleanPath.includes(Route.heatmap)) {
    return 'heatmap';
  } else if (cleanPath.includes(Route.deploy)) {
    return 'deploy';
  } else if (cleanPath.includes(Route.monitor)) {
    return 'monitor';
  } else {
    return 'default';
  }
}
